import * as React from 'react';
import { useEffect, useState } from 'react';
import { Card, Dropdown, DropdownButton, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { formatDate, getStatusLabel, hasAccessRight } from '../../../utils/Functions';
import { ImpersonateEye } from '../../../shared/components/ImpersonateEye';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { FiInfo } from 'react-icons/fi';
import { ContractService } from '../ContractService';
import { ContractFilter } from './ContractFilter';
import { Contract } from '../../../model/contract/Contract';
import { ErrorToast } from '../../../utils/toasters';
import TableLayout from '../../../shared/layout/table/TableLayout';
import { ITableColumn } from '../../../shared/components/TableSkeleton';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { UPDATED_CONTRACT } from '../../../enum/Messages';
import { PurchaseOrderModal } from '../../../shared/components/PurchaseOrderModal';
import { downloadFileRequest } from '../../../api/api.service';
import { OverviewModal } from '../../../shared/components/OverviewModal';
import { AdminAclEnum } from '../../../enum/AdminAclEnum';
import { CONTRACT_LIST_DOWNLOAD_URL, LIST_CONTRACT_URL } from '../../../utils/Endpoints';

const CONTRACT_COLUMNS: ITableColumn[] = [
    { label: 'Date' },
    { label: 'Contract ID' },
    { label: 'Company' },
    { label: 'Influencer' },
    { label: 'Campaign' },
    { label: 'Status' },
    { label: 'Purchase Order' },
    { label: 'Agency?' },
    { label: 'Current?' },
    { label: 'Offers' },
    { label: 'Action' },
];

export function ContractList() {
    const navigate = useNavigate();
    const [dataLoading, setDataLoading] = useState<boolean>(true);
    const [isExporting, setIsExporting] = useState(false);
    const [overviewContractId, setOverviewContractId] = useState<number | null>(null);
    const [contracts, setContracts] = useState<Contract[]>([]);
    const [offersFilter, setState] = useState({
        page: 1,
        perPage: 15,
        id: '',
        date: '',
        influencerId: '',
        campaignId: '',
        companyId: '',
        status: '',
        totalPages: 0,
    });
    const [showPurchaseOrderModal, setShowPurchaseOrderModal] = useState(false);
    const [purchaseOrderContract, setPurchaseOrderContract] = useState<Contract | null>(null);

    function getContract(offersFilter: any, updatePage?: number) {
        setDataLoading(true);
        ContractService.getContracts({ ...offersFilter, page: !!updatePage ? updatePage : offersFilter.page })
            .then(data => {
                setContracts(data?.data);
                setState({ ...offersFilter, page: data.page });
                setState(prev => ({ ...prev, totalPages: Math.ceil(data.count / offersFilter.perPage) }));
            })
            .catch(err => ErrorToast(err))
            .finally(() => setDataLoading(false));
    }

    useEffect(() => {
        getContract(offersFilter);
    }, [offersFilter.campaignId, offersFilter.influencerId, offersFilter.companyId]);

    const handleExport = () => {
        const params = Object.fromEntries(
            Object.entries(offersFilter).filter(
                ([key, value]) =>
                    !['page', 'perPage', 'totalPages'].includes(key) && value !== '' && value !== undefined
            )
        );
        downloadFileRequest(CONTRACT_LIST_DOWNLOAD_URL, 'export-contracts', null, params, setIsExporting);
    };

    const handlePages = (updatePage: number) => {
        getContract(offersFilter, updatePage);
    };

    const handleSavePurchaseOrder = (purchaseOrderInfluencer: string) => {
        if (!purchaseOrderContract) return;

        ContractService.updatePurchaseOrder(purchaseOrderContract.id, purchaseOrderInfluencer)
            .then(() => {
                toast(UPDATED_CONTRACT);
                getContract(offersFilter);
            })
            .catch(err => ErrorToast(err));
    };

    return (
        <>
            <Card
                className="mb-5 p-4"
                onKeyDown={(e: any) => {
                    if (e.key === 'Enter') getContract(offersFilter);
                }}
            >
                <ContractFilter
                    handleState={getContract}
                    setState={setState}
                    offersFilter={offersFilter}
                    onExport={handleExport}
                    isExporting={isExporting}
                    isDisabled={dataLoading}
                />
            </Card>

            <OverviewModal
                show={overviewContractId !== null}
                id={overviewContractId}
                title="Contract"
                fetchOverview={ContractService.getContractOverview}
                storedPdfUrl={pdfId => `${LIST_CONTRACT_URL}/pdf-file/${pdfId}`}
                generatedPdfUrl={contractId => `${LIST_CONTRACT_URL}/pdf/${contractId}/pdf`}
                onHide={() => setOverviewContractId(null)}
            />

            <PurchaseOrderModal
                show={showPurchaseOrderModal}
                id={purchaseOrderContract?.id}
                initialValue={purchaseOrderContract?.purchaseOrderInfluencer}
                onHide={() => setShowPurchaseOrderModal(false)}
                onSubmit={handleSavePurchaseOrder}
            />

            <TableLayout
                pagination={offersFilter}
                listLength={contracts.length}
                handlePages={handlePages}
                isLoading={dataLoading}
                dataName={'contracts'}
                skeletonColumns={CONTRACT_COLUMNS}
                skeletonRows={offersFilter.perPage}
            >
                <Table hover responsive>
                    <thead>
                        <tr>
                            {CONTRACT_COLUMNS.map(column => (
                                <th key={column.label} className={column.className}>
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {contracts.map(e => (
                            <tr key={e?.id}>
                                <td>{formatDate(e.createdAt)}</td>
                                <td>{e.id}</td>
                                <td>
                                    <ImpersonateEye id={e?.campaign?.company?.createdBy} />
                                    {e?.campaign?.company?.displayName}
                                </td>
                                <td>
                                    <ImpersonateEye id={e?.user?.id ?? 0} />
                                    {e?.user?.displayName}
                                    <br />
                                    {e?.user?.firstName ?? '-'} {e?.user?.lastName}
                                </td>
                                <td>
                                    {e?.campaign?.id}
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id="campaign-tooltip">{e?.campaign?.title}</Tooltip>}
                                    >
                                        <span className="ml-1">
                                            <FiInfo size={14} className="cursor-pointer" />
                                        </span>
                                    </OverlayTrigger>
                                </td>
                                <td>
                                    <span className={`status ${e?.status}`}>
                                        {e?.isAnnex && ['accepted', 'completed', 'annexed'].includes(e?.status)
                                            ? `Amendment ${e?.annexNumber}`
                                            : getStatusLabel(e?.status)}
                                    </span>
                                </td>
                                <td>{e?.purchaseOrderInfluencer || '-'}</td>
                                <td>
                                    {e?.user?.agencyCompany?.id ? (
                                        e?.user?.agencyCompany?.id
                                    ) : (
                                        <span className="word-break text-danger">
                                            <FaTimes />
                                        </span>
                                    )}
                                    {e?.user?.agencyCompany?.id && (
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={
                                                <Tooltip id="campaign-tooltip">
                                                    {e?.user?.agencyCompany?.displayName}
                                                </Tooltip>
                                            }
                                        >
                                            <span className="ml-1">
                                                <FiInfo size={14} className="cursor-pointer" />
                                            </span>
                                        </OverlayTrigger>
                                    )}
                                </td>
                                <td>
                                    {e?.isCurrent ? (
                                        <span className="word-break text-primary">
                                            <FaCheck />
                                        </span>
                                    ) : (
                                        <span className="word-break text-danger">
                                            <FaTimes />
                                        </span>
                                    )}
                                </td>
                                <td>{e?.offers?.length ?? 0}</td>
                                <td>
                                    <DropdownButton title="Actions" id="dropdown-menu-align-right">
                                        {hasAccessRight(AdminAclEnum.CONTRACT_OVERVIEW) && (
                                            <Dropdown.Item onClick={() => setOverviewContractId(e.id)}>
                                                Overview
                                            </Dropdown.Item>
                                        )}
                                        <Dropdown.Item
                                            onClick={() =>
                                                navigate(`/contracts/edit/`, { state: { id: e.id, offer: e } })
                                            }
                                        >
                                            Edit
                                        </Dropdown.Item>
                                        {e.status === 'accepted' && (
                                            <Dropdown.Item onClick={() => navigate(`/contracts/edit/schedule/${e.id}`)}>
                                                Edit invoice schedule
                                            </Dropdown.Item>
                                        )}
                                        <Dropdown.Item
                                            onClick={() => {
                                                setPurchaseOrderContract(e);
                                                setShowPurchaseOrderModal(true);
                                            }}
                                        >
                                            Add purchase order
                                        </Dropdown.Item>
                                    </DropdownButton>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    {contracts.length < 15 && !!contracts.length && <div className="h-180px"></div>}
                </Table>
            </TableLayout>
        </>
    );
}
